import { notifications } from '@mantine/notifications';
import type { CoordinatorGrant, CoordinatorRole } from '@reditelji/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { ME_QUERY_KEY, useSession } from './session';

/** A login on Nastavitve → Uporabniki (SPEC §1.1L). */
export interface CoordinatorRow {
  id: string;
  email: string;
  name: string;
  role: CoordinatorRole;
  grantedAt: string | null;
  disabledAt: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  /** On COORDINATOR_EMAILS — admitted without a grant. */
  fromEnv: boolean;
  canLogIn: boolean;
}

export function useCoordinators() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['coordinators'],
    queryFn: () => api<CoordinatorRow[]>('/coordinators'),
    enabled: can('access:manage'),
  });
}

/** One login by name alone — what every screen that shows a coordinator reads. */
export interface CoordinatorName {
  id: string;
  name: string;
}

/** Under `['coordinators']`, so everything that refreshes Uporabniki refreshes the names too. */
export const COORDINATOR_NAMES_KEY = ['coordinators', 'names'] as const;

export function useCoordinatorNames() {
  return useQuery({
    queryKey: COORDINATOR_NAMES_KEY,
    queryFn: () => api<CoordinatorName[]>('/coordinators/names'),
    staleTime: 5 * 60 * 1000,
  });
}

function onError(err: Error) {
  // The API's refusals here are sentences meant for the person clicking.
  notifications.show({ color: 'red', title: 'Ni shranjeno', message: err.message });
}

export function useCoordinatorMutations() {
  const qc = useQueryClient();
  const common = {
    onError,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['coordinators'] });
      void qc.invalidateQueries({ queryKey: ['changelog'] });
    },
  };
  const grant = useMutation({
    mutationFn: (input: CoordinatorGrant) =>
      api<CoordinatorRow>('/coordinators', { method: 'POST', body: JSON.stringify(input) }),
    ...common,
  });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: CoordinatorRole }) =>
      api<CoordinatorRow>(`/coordinators/${id}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    ...common,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<CoordinatorRow>(`/coordinators/${id}/revoke`, { method: 'POST' }),
    ...common,
  });
  const restore = useMutation({
    mutationFn: (id: string) => api<CoordinatorRow>(`/coordinators/${id}/restore`, { method: 'POST' }),
    ...common,
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api<CoordinatorRow>(`/coordinators/${id}/name`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    ...common,
    onSettled: () => {
      common.onSettled();
      // Renaming yourself on Uporabniki is still renaming yourself.
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
  return { grant, setRole, revoke, restore, rename };
}

/** Set one's own full name — the first-login dialog and "Moje ime…". */
export function useRenameMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api<unknown>('/auth/me', { method: 'PATCH', body: JSON.stringify({ name }) }),
    onError,
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['coordinators'] });
      void qc.invalidateQueries({ queryKey: ['changelog'] });
    },
  });
}
